import { h } from '../lib/dom.js';
import { getState, setState, CONFIG } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import {
  togglePrivateKey, openImportKey, goLock, setTheme,
  setTestnetEnabled, requestMainnetSwitch, setDefaultStablecoin, setDefaultChain,
  startProfileEdit, setProfileEditDraft, confirmProfileEdit, cancelProfileEdit,
  toggleRecoveryInfo, setProfileAvatar, copyLlmsTxt, setLanguage,
  setAutoLockMinutes, AUTO_LOCK_MINUTE_OPTIONS, setWipeAfterN, WIPE_AFTER_OPTIONS,
  requestVerifyBackup, dismissBackupPrompt, openMigrateTreasury
} from '../actions.js';
import { t, supportedLanguages } from '../i18n/index.js';

// Icon toggle-right (blue, on) / toggle-left (grey, off) switches, per
// feature-ui.md's explicit follow-up spec — a real <button> (not a bare
// icon) so it stays a focusable, keyboard-activatable control.
function toggleButton(label, on, onclick) {
  return h('button', {
    class: 'icon-btn-plain', 'aria-label': label, 'aria-pressed': on ? 'true' : 'false', onclick
  }, h('span', { html: iconMarkup(on ? 'toggleRight' : 'toggleLeft', { size: 28, color: on ? '#4D4DFF' : '#757993' }) }));
}

function profileField(s, p, field, label) {
  const editing = s.profileEdit.profileId === p.id && s.profileEdit.field === field;
  if (editing) {
    return h('div', {}, [
      h('div', { class: 'card-label' }, label),
      h('div', { class: 'field-row' }, [
        h('input', {
          value: s.profileEdit.draft,
          oninput: (e) => setProfileEditDraft(e.target.value),
          onkeydown: (e) => { if (e.key === 'Enter') confirmProfileEdit(); if (e.key === 'Escape') cancelProfileEdit(); }
        }),
        h('button', { class: 'btn btn-primary btn-sm', onclick: confirmProfileEdit }, t('common.save')),
        h('button', { class: 'btn btn-secondary btn-sm', onclick: cancelProfileEdit }, t('common.cancel'))
      ]),
      s.profileEdit.error ? h('div', { class: 'field-error' }, s.profileEdit.error) : null
    ]);
  }
  const value = field === 'name' ? p.name : (p.email || '—');
  return h('div', {}, [
    h('div', { class: 'card-label' }, label),
    h('div', { class: 'flex-between items-center' }, [
      h('span', {}, value),
      h('button', { class: 'icon-btn', 'aria-label': t('common.editField', { label }), onclick: () => startProfileEdit(p.id, field) },
        h('span', { html: iconMarkup('pencil', { size: 14 }) }))
    ])
  ]);
}

function avatarPicker(p) {
  return h('div', {}, [
    h('div', { class: 'card-label' }, t('settings.avatar')),
    h('div', { class: 'flex-gap-sm avatar-picker' }, CONFIG.AVATARS.map((a) =>
      h('button', {
        class: 'icon-btn-plain avatar-option' + (a === p.avatar ? ' avatar-option-selected' : ''),
        'aria-label': a, 'aria-pressed': a === p.avatar ? 'true' : 'false',
        onclick: () => setProfileAvatar(p.id, a)
      }, h('span', { html: iconMarkup(a, { size: 20, color: a === p.avatar ? '#4D4DFF' : '#757993' }) }))
    ))
  ]);
}

export function renderSettings() {
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];

  // Real currency list once live rates exist (feature-backend.md item 7:
  // "widen beyond G20 to the full list available from frankfurter") — the
  // reporting currency can only usefully be one frankfurter actually
  // prices. Falls back to CONFIG.FALLBACK_REPORTING_CURRENCIES only in the
  // brief window before the first fetch (or its localStorage-cached
  // predecessor) resolves.
  const reportingCurrencies = s.fxRates && s.fxRates.rates
    ? Object.keys(s.fxRates.rates).sort()
    : CONFIG.FALLBACK_REPORTING_CURRENCIES;
  const currencySelect = h('select', {
    onchange: (e) => setState({ reportingCurrency: e.target.value })
  }, reportingCurrencies.map((cc) => h('option', { value: cc, selected: cc === s.reportingCurrency || undefined }, cc)));

  const themeSelect = h('select', { onchange: (e) => setTheme(e.target.value) }, [
    h('option', { value: 'system', selected: s.theme === 'system' || undefined }, t('settings.matchDevice')),
    h('option', { value: 'light', selected: s.theme === 'light' || undefined }, t('settings.light')),
    h('option', { value: 'dark', selected: s.theme === 'dark' || undefined }, t('settings.dark'))
  ]);

  const languageSelect = h('select', { onchange: (e) => setLanguage(e.target.value) },
    supportedLanguages().map((l) => h('option', { value: l.code, selected: l.code === s.language || undefined }, l.label)));

  const stablecoinOptions = Object.keys(CONFIG.CHAINS);
  const defaultCcySelect = h('select', { onchange: (e) => setDefaultStablecoin(e.target.value) },
    stablecoinOptions.map((c) => h('option', { value: c, selected: c === s.defaultStablecoin || undefined }, c)));
  const defaultChainOptions = CONFIG.CHAINS[s.defaultStablecoin] || [];
  const defaultChainSelect = h('select', { onchange: (e) => setDefaultChain(e.target.value) },
    defaultChainOptions.map((c) => h('option', { value: c, selected: c === s.defaultChain || undefined }, c)));

  const autoLockSelect = h('select', { onchange: (e) => setAutoLockMinutes(Number(e.target.value)) },
    AUTO_LOCK_MINUTE_OPTIONS.map((m) => h('option', { value: m, selected: m === s.autoLockMinutes || undefined }, t('settings.autoLockMinutes', { minutes: m }))));

  // feature-harden.md 6.1: opt-in, off (0) by default — never destructive
  // unless the user explicitly turns it on, per the doc's own "opt-in"
  // wording. Reads from the active profile's own security.wipeAfterN, not
  // a global setting — each profile on this device makes its own call.
  const wipeAfterN = (profile && profile.security && profile.security.wipeAfterN) || 0;
  const wipeAfterSelect = h('select', { onchange: (e) => setWipeAfterN(Number(e.target.value)) },
    WIPE_AFTER_OPTIONS.map((n) => h('option', { value: n, selected: n === wipeAfterN || undefined },
      n === 0 ? t('settings.wipeAfterOff') : t('settings.wipeAfterN', { n }))));

  const primaryKey = s.privateKeys[0];

  return h('div', { class: 'screen form-column' }, [
    h('h1', { class: 'screen-title' }, t('settings.title')),

    profile ? h('div', { class: 'card stack' }, [
      h('div', { class: 'card-title-sm' }, t('settings.profile')),
      profileField(s, profile, 'name', t('settings.name')),
      !profile.anonymous ? profileField(s, profile, 'email', t('settings.email')) : null,
      avatarPicker(profile)
    ]) : null,

    h('div', { class: 'card' }, [
      h('div', { class: 'card-label' }, t('settings.reportingCurrency')),
      currencySelect
    ]),
    h('div', { class: 'card' }, [
      h('div', { class: 'card-label' }, t('settings.appearance')),
      themeSelect
    ]),
    h('div', { class: 'card' }, [
      h('div', { class: 'card-label' }, t('settings.language')),
      languageSelect
    ]),
    h('div', { class: 'card stack' }, [
      h('div', { class: 'card-label' }, t('settings.defaultStablecoin')),
      defaultCcySelect,
      h('div', { class: 'card-label mt-sm' }, t('settings.defaultChain')),
      defaultChainSelect
    ]),

    h('div', { class: 'card' }, [
      h('div', { class: 'card-label' }, t('settings.autoLock')),
      autoLockSelect
    ]),

    h('div', { class: 'card stack' }, [
      h('div', { class: 'card-label' }, t('settings.wipeAfter')),
      h('div', { class: 'text-muted' }, t('settings.wipeAfterHelp')),
      wipeAfterSelect
    ]),

    h('div', { class: 'card flex-between items-center' }, [
      h('div', { class: 'card-label' }, t('settings.testnet')),
      // Turning testnet back ON is the safe direction and applies
      // instantly; turning it OFF (opting into mainnet — real funds)
      // routes through an explicit confirmation step first (CLAUDE.md —
      // see store.js's showMainnetConfirm comment).
      toggleButton(t('settings.testnet'), s.testnetEnabled, () => (s.testnetEnabled ? requestMainnetSwitch() : setTestnetEnabled(true)))
    ]),

    h('div', { class: 'card flex-between items-center' }, [
      h('div', { class: 'card-label' }, t('settings.llmsTxt')),
      h('div', { class: 'flex-gap-sm' }, [
        h('a', {
          class: 'icon-btn-plain', 'aria-label': t('settings.downloadLlmsTxt'),
          href: '/llms.txt', download: 'llms.txt'
        }, h('span', { html: iconMarkup('fileText', { size: 18, color: '#757993' }) })),
        h('button', {
          class: 'icon-btn-plain', 'aria-label': t('settings.copyLlmsTxt'), onclick: copyLlmsTxt
        }, h('span', { html: iconMarkup('clipboardCopy', { size: 18, color: '#757993' }) }))
      ])
    ]),

    h('div', { class: 'card stack' }, [
      h('div', { class: 'flex-gap-sm items-center' }, [
        h('div', { class: 'card-label' }, t('settings.accountRecovery')),
        h('button', {
          class: 'icon-btn-plain', 'aria-label': t('settings.accountRecoveryInfoTitle'), onclick: toggleRecoveryInfo
        }, h('span', { html: iconMarkup('circleQuestionMark', { size: 14, color: '#757993' }) }))
      ]),
      s.showRecoveryInfo ? h('div', { class: 'text-sm text-muted' },
        t('settings.accountRecoveryInfo')
      ) : null,
      primaryKey && primaryKey.revealed && primaryKey.words ? h('div', { class: 'stack' }, [
        h('div', { class: 'word-grid' },
          primaryKey.words.map((w, i) => h('div', { class: 'word' }, [h('span', { class: 'n' }, `${i + 1}.`), ` ${w}`]))),
        // CCSS-BR (feature-harden.md): a clear on-screen warning is the
        // realistic bar here -- there's no reliable cross-platform web API
        // to actually detect or block an OS-level screenshot from a page.
        h('div', { class: 'backup-screenshot-warning' }, [
          h('span', { html: iconMarkup('triangleAlert', { size: 16, color: '#c0392b' }) }),
          h('span', {}, t('settings.screenshotWarning'))
        ]),
        // Guided backup ceremony: never forced -- "Skip" always sits right
        // next to "Verify backup", one tap, no follow-up prompt (user
        // instruction, 2026-08-05).
        primaryKey.backupVerified
          ? h('div', { class: 'backup-verified-badge' }, [
              h('span', { html: iconMarkup('shieldCheck', { size: 14, color: '#1a8a4c' }) }),
              h('span', {}, t('settings.backupVerified'))
            ])
          : (primaryKey.backupPromptDismissed ? null : h('div', { class: 'flex-gap-sm items-center' }, [
              h('button', { class: 'btn btn-secondary btn-sm', onclick: () => requestVerifyBackup(primaryKey.id) }, t('settings.verifyBackup')),
              h('button', { class: 'btn-link text-muted', onclick: () => dismissBackupPrompt(primaryKey.id) }, t('settings.skipBackupVerify'))
            ]))
      ]) : null,
      h('div', { class: 'btn-row' }, [
        h('button', { class: 'btn btn-secondary', onclick: openImportKey }, t('settings.import')),
        primaryKey ? h('button', {
          class: 'btn btn-primary',
          onclick: () => togglePrivateKey(primaryKey.id)
        }, primaryKey.revealed ? t('settings.hide') : t('settings.reveal')) : null
      ])
    ]),

    // RECOVERY.md's "if your device or passcode is compromised" flow
    // (feature-outstanding.md, 2026-08-07) — placed next to Lock, the
    // other security-response action on this screen, not buried in a
    // separate section.
    h('div', { class: 'card stack' }, [
      h('div', { class: 'card-label' }, t('settings.migrateTreasury')),
      h('div', { class: 'text-sm text-muted' }, t('settings.migrateTreasuryHelp')),
      h('button', { class: 'btn btn-secondary', onclick: openMigrateTreasury }, t('settings.migrateTreasury'))
    ]),

    h('button', { class: 'btn btn-secondary text-muted', onclick: goLock }, t('settings.lock'))
  ]);
}
